import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Contacts as ContactsIcon,
  Receipt as ReceiptIcon,
  Euro as EuroIcon,
  AccountBalance as BalanceIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';

const AccountantDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalClients: 12,
    activeClients: 11,
    revenue: 224000,
    netProfit: 76000,
    outstandingReceivables: 189300,
    outstandingPayables: 54200,
    cashBalance: 384000,
    vatReturnsDue: 3,
  });

  useEffect(() => {
    // Simulate loading data
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);

  const statCards = [
    {
      title: t('total_clients'),
      value: stats.totalClients,
      change: '+2 this quarter',
      icon: <ContactsIcon sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
      bgColor: '#E8F5E9',
    },
    {
      title: t('active_clients'),
      value: stats.activeClients,
      change: '1 onboarding',
      icon: <ContactsIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
      bgColor: '#E3F2FD',
    },
    {
      title: t('revenue_june'),
      value: `€${stats.revenue.toLocaleString()}`,
      change: '+23% vs May',
      icon: <EuroIcon sx={{ fontSize: 40 }} />,
      color: '#FF9800',
      bgColor: '#FFF3E0',
      trend: 'up',
    },
    {
      title: t('net_profit'),
      value: `€${stats.netProfit.toLocaleString()}`,
      change: '+5% vs May',
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
      bgColor: '#E8F5E9',
      trend: 'up',
    },
    {
      title: t('outstanding_receivables'),
      value: `€${stats.outstandingReceivables.toLocaleString()}`,
      change: '-4% 42 invoices',
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
      color: '#9C27B0',
      bgColor: '#F3E5F5',
      trend: 'down',
    },
    {
      title: t('outstanding_payables'),
      value: `€${stats.outstandingPayables.toLocaleString()}`,
      change: '-46% 18 invoices',
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
      color: '#F44336',
      bgColor: '#FFEBEE',
      trend: 'down',
    },
    {
      title: t('cash_balance'),
      value: `€${stats.cashBalance.toLocaleString()}`,
      change: '+23% across accounts',
      icon: <BalanceIcon sx={{ fontSize: 40 }} />,
      color: '#00BCD4',
      bgColor: '#E0F7FA',
    },
    {
      title: t('vat_returns_due'),
      value: stats.vatReturnsDue,
      change: 'next: Jul 19',
      icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
      color: '#673AB7',
      bgColor: '#EDE7F6',
    },
  ];

  if (loading) {
    return (
      <AccountantLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </AccountantLayout>
    );
  }

  return (
    <AccountantLayout>
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            {t('accountant_dashboard')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('dashboard_subtitle')}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {statCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box
                      sx={{
                        backgroundColor: card.bgColor,
                        borderRadius: '12px',
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {React.cloneElement(card.icon, { sx: { fontSize: 40, color: card.color } })}
                    </Box>
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {card.value}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {card.trend === 'up' && (
                      <TrendingUp sx={{ fontSize: 16, color: '#4CAF50' }} />
                    )}
                    {card.trend === 'down' && (
                      <TrendingDown sx={{ fontSize: 16, color: '#F44336' }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: card.trend === 'up' ? '#4CAF50' : card.trend === 'down' ? '#F44336' : 'textSecondary',
                      }}
                    >
                      {card.change}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </AccountantLayout>
  );
};

export default AccountantDashboard;
